import type { Polygon, Bbox } from "./geo";
import { polygonBbox } from "./geo";

export class GeoJsonParseError extends Error {}

/** A successful import: the projected polygon ready for storage on
 * `location.polygon`, plus the original parsed object for
 * `location.polygon_source_geojson`. */
export interface ImportedPolygon {
  polygon: Array<[number, number]>;
  source: unknown;
}

interface RawRing {
  /** First (outer) ring of a GeoJSON Polygon: an array of [x, y] pairs. */
  ring: Array<[number, number]>;
}

function extractFirstPolygon(input: unknown): RawRing {
  if (!input || typeof input !== "object") {
    throw new GeoJsonParseError("expected a GeoJSON object");
  }
  const obj = input as Record<string, unknown>;
  const type = obj.type;

  if (type === "FeatureCollection") {
    const features = Array.isArray(obj.features) ? obj.features : [];
    for (const f of features) {
      if (f && typeof f === "object" && (f as Record<string, unknown>).type === "Feature") {
        const geom = (f as Record<string, unknown>).geometry;
        try {
          return extractFirstPolygon(geom);
        } catch {
          // try next feature
        }
      }
    }
    throw new GeoJsonParseError("no Polygon feature found in FeatureCollection");
  }

  if (type === "Feature") {
    return extractFirstPolygon(obj.geometry);
  }

  if (type === "Polygon") {
    const coords = obj.coordinates;
    if (!Array.isArray(coords) || coords.length === 0) {
      throw new GeoJsonParseError("Polygon has no coordinates");
    }
    return { ring: parseRing(coords[0]) };
  }

  if (type === "MultiPolygon") {
    const coords = obj.coordinates;
    if (!Array.isArray(coords) || coords.length === 0) {
      throw new GeoJsonParseError("MultiPolygon has no coordinates");
    }
    const first = coords[0];
    if (!Array.isArray(first) || first.length === 0) {
      throw new GeoJsonParseError("MultiPolygon[0] has no rings");
    }
    return { ring: parseRing(first[0]) };
  }

  throw new GeoJsonParseError(
    `unsupported GeoJSON type ${typeof type === "string" ? `"${type}"` : "(missing)"}`,
  );
}

function parseRing(raw: unknown): Array<[number, number]> {
  if (!Array.isArray(raw)) {
    throw new GeoJsonParseError("ring must be an array");
  }
  const out: Array<[number, number]> = [];
  for (const pt of raw) {
    if (!Array.isArray(pt) || pt.length < 2 || typeof pt[0] !== "number" || typeof pt[1] !== "number") {
      throw new GeoJsonParseError("ring points must be [x, y] number pairs");
    }
    out.push([pt[0], pt[1]]);
  }
  if (out.length < 3) {
    throw new GeoJsonParseError("ring needs at least 3 points");
  }
  // GeoJSON closes rings (first == last). Drop the duplicate; SVG path closes via "Z".
  const a = out[0];
  const b = out[out.length - 1];
  if (a[0] === b[0] && a[1] === b[1]) {
    out.pop();
  }
  return out;
}

/** Heuristic: lon/lat coordinates have small absolute values
 * (|lon| ≤ 180, |lat| ≤ 90). Garden-local viewport coords are larger.
 * If the input bbox sits within geographic ranges, treat it as
 * lat/lon and flip the Y axis (GeoJSON is north-up; SVG is y-down). */
function looksGeographic(bbox: Bbox): boolean {
  return (
    Math.abs(bbox.minX) <= 180 &&
    Math.abs(bbox.maxX) <= 180 &&
    Math.abs(bbox.minY) <= 90 &&
    Math.abs(bbox.maxY) <= 90
  );
}

/** Projects a raw GeoJSON ring into the viewport.
 *
 * Strategy: scale-fit the polygon's bbox into a `targetSize`×`targetSize`
 * box centered on the viewport. Flip Y when the input looks like lat/lon.
 * The user can fine-tune position later by dragging pins; v1 has no
 * polygon-drag, so this is the entry point.
 */
function projectRingToViewport(
  ring: Array<[number, number]>,
  viewport: { width: number; height: number },
  targetSize: number,
): Array<[number, number]> {
  const bbox = polygonBbox(ring as Polygon);
  const w = bbox.maxX - bbox.minX || 1;
  const h = bbox.maxY - bbox.minY || 1;
  const scale = Math.min(targetSize / w, targetSize / h);
  const flipY = looksGeographic(bbox);
  const scaledW = w * scale;
  const scaledH = h * scale;
  const offsetX = viewport.width / 2 - scaledW / 2;
  const offsetY = viewport.height / 2 - scaledH / 2;
  return ring.map(([x, y]) => {
    const lx = (x - bbox.minX) * scale;
    const ly = flipY ? (bbox.maxY - y) * scale : (y - bbox.minY) * scale;
    return [
      Math.round((offsetX + lx) * 100) / 100,
      Math.round((offsetY + ly) * 100) / 100,
    ] as [number, number];
  });
}

export function parseGeoJsonForBed(
  text: string,
  viewport: { width: number; height: number },
  options?: { targetSize?: number },
): ImportedPolygon {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (err) {
    throw new GeoJsonParseError(
      err instanceof Error ? `not valid JSON: ${err.message}` : "not valid JSON",
    );
  }
  const { ring } = extractFirstPolygon(parsed);
  const targetSize = options?.targetSize ?? 220;
  const projected = projectRingToViewport(ring, viewport, targetSize);
  return { polygon: projected, source: parsed };
}
